import { useGetVehicleByIdQuery } from '../../../../../services/api/upload-vehicle';
import { useGetVehicleId } from '../../../../CreateVehicle/hooks/useGetVehicleId';
import styled from 'styled-components';
import { useEffect, useMemo, useRef, useState } from 'react';
import { openFullScreenWithId, useImages } from '../../../../../services/viewVehicleSlice/viewVehicleSlice';
import { useDispatch } from 'react-redux';
import ReactImageGallery from 'react-image-gallery';
import { useCurrentUser } from '../../../../../hooks/selectors';
import { BiddingLabel } from './components/BiddingLabel';
import { useExteriorVideo } from '../VehicleSections/hooks/useExteriorVideo';
import AutoPlayVideo from 'components/AutoPlayVideo';
import { VehicleImageCarousel } from 'components/VehicleImageCarousel';
import { useDeviceInfo } from 'hooks/useDeviceInfo';

const Wrapper = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  gap: 16px;
  max-width: 640px;

  @media (min-width: 1800px) {
    max-width: 800px;
  }
`;

const PreviewImage = styled.div<{ showNextButton?: boolean }>`
  border-radius: 30px;
  position: relative;
  aspect-ratio: 4 / 3;

  & > img {
    border: 1px solid rgba(204, 204, 204, 1);
    border-radius: 30px;
    width: 100%;
    height: 100%;
    opacity: ${props => (props.showNextButton ? '0.6' : '1')};
    cursor: pointer;
  }

  @media (max-width: 1024px) {
    & > img {
      border-radius: 20px;
    }
  }
`;

const videoStyles = {
  borderRadius: '30px',
  position: 'relative',
  objectFit: 'cover',
  aspectRatio: '4/3'
};

const PreviewVideo = styled(AutoPlayVideo)`
  video {
    width: 100%;
    min-width: 300px;
    border-radius: 30px;
    position: relative;
    object-fit: cover;
    aspect-ratio: 4 / 3;
  }
`;

const PlayImageWrapper = styled.div`
  position: absolute;
  top: 50%;
  cursor: pointer;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const ImagePreviewWrapper = styled.div<{ expanded?: boolean }>`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: calc((100% - (3 * 8px)) / 4);
  gap: 8px;
  overflow-x: ${props => (props.expanded ? 'auto' : 'hidden')};

  @media (max-width: 1024px) {
    grid-auto-columns: calc((100% - (3 * 4px)) / 4);
    gap: 4px;
  }
`;

const Plus4Label = styled.div`
  font-weight: 700;
  font-style: Bold;
  font-size: 32px;
  line-height: 100%;
  color: #fff;
`;

type Props = {
  imageGalleryRef: React.RefObject<ReactImageGallery>;
};

export const ImageCarousel = ({ imageGalleryRef }: Props) => {
  const vehicleId = useGetVehicleId();
  const { data: vehicle } = useGetVehicleByIdQuery(vehicleId, { skip: !vehicleId });

  const currentUser = useCurrentUser();

  const images = useImages();
  const mappedImages = useMemo(
    () => images.map(i => ({ original: i.url!, thumbnail: i.url_thumb, description: i.notes?.message || '' })),
    [images.length]
  );

  const dispatch = useDispatch();

  if (!vehicle) return null;

  const handleImageClick = () => {
    // @ts-expect-error invalid prop types
    const index = imageGalleryRef.current?.state?.currentIndex;
    if (typeof index !== 'number') return;
    dispatch(openFullScreenWithId(images[index].id));
  };

  return (
    <Wrapper>
      {images.length > 0 && (
        <VehicleImageCarousel images={images} imageGalleryRef={imageGalleryRef} handleClick={handleImageClick} />
      )}
      {currentUser.role === 'buyer' && <BiddingLabel vehicle={vehicle} />}
      <ImagePreviewComponent />
    </Wrapper>
  );
};

const ImagePreviewComponent = () => {
  const [isExpanded, setIsExpanded] = useState(false);

  const dispatch = useDispatch();

  const previewContainerRef = useRef<HTMLDivElement>(null);

  const images = useImages();
  const video = useExteriorVideo();

  useEffect(() => {
    if (!previewContainerRef.current || !isExpanded) {
      return;
    }

    previewContainerRef.current.scrollTo({
      left: 1 * previewContainerRef.current.clientWidth + 8,
      behavior: 'smooth'
    });
  }, [isExpanded]);

  const { isMobile } = useDeviceInfo();

  const totalItems = images.length + +Boolean(video);
  const plusButtonIndex = !!video && !isMobile ? 2 : 3;

  return (
    <ImagePreviewWrapper ref={previewContainerRef} expanded={isExpanded}>
      {video && !isMobile && <PreviewVideo src={video} style={videoStyles} />}
      {images.map((img, i) => {
        const showPlusNButton = i === plusButtonIndex && totalItems > 4 && !isExpanded;
        const handleClick = () => {
          if (showPlusNButton) {
            setIsExpanded(true);
          } else {
            dispatch(openFullScreenWithId(img.id));
          }
        };
        return (
          <PreviewImage key={img.id} showNextButton={showPlusNButton} onClick={handleClick}>
            <img src={img.url_thumb} alt={img.id.toString()} />
            {showPlusNButton && (
              <PlayImageWrapper>
                <Plus4Label>{`+${totalItems - 4}`}</Plus4Label>
              </PlayImageWrapper>
            )}
          </PreviewImage>
        );
      })}
    </ImagePreviewWrapper>
  );
};
